import React, { FC, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    ControlledAutocomplete,
    ControlledCheckbox,
    ControlledInput,
    FormGrid,
    ISupportPeriod,
    Section,
    sortByName,
} from '../../../shared';
import { referrerContactFormatter } from '../../formatters';
import { useReferralOrganisationList } from '../../../admin/hooks/referral-organisation.hook';

interface Props {
    supportPeriod?: ISupportPeriod | null;
}

export const ReferrerInputs: FC<Props> = ({ supportPeriod }) => {
    const { t } = useTranslation();
    const { data: referralOrganisations } = useReferralOrganisationList({ pageSize: 500 });

    const { watch, setValue } = useFormContext();
    const watchReferralOrganisation = watch('referralOrganisation');
    const watchNewContact = watch('isNewContact');

    useEffect(() => {
        if (watchReferralOrganisation === undefined) {
            setValue('contact', supportPeriod?.contact || null);
        } else if (watchReferralOrganisation?.id !== supportPeriod?.referralOrganisation?.id) {
            setValue('contact', null);
        }
    }, [watchReferralOrganisation, supportPeriod, setValue]);

    const referrerContacts = useMemo(() => {
        return (
            referralOrganisations?.data
                .find((org) => org.id === watchReferralOrganisation?.id)
                ?.contacts.sort(sortByName) || []
        );
    }, [referralOrganisations, watchReferralOrganisation]);

    return (
        <Section title={t('contact')}>
            <FormGrid size={{ xs: 12, md: 6 }}>
                <ControlledAutocomplete
                    name="referralOrganisation"
                    label={t('referralOrganisation')}
                    options={referralOrganisations?.data || []}
                    getOptionLabel={(option) => `${option.name}`}
                    defaultValue={null}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                />

                {!watchNewContact && (
                    <ControlledAutocomplete
                        name="contact"
                        label={t('referrerContact')}
                        options={referrerContacts}
                        getOptionLabel={(option) => referrerContactFormatter(option)}
                        defaultValue={null}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                )}
            </FormGrid>

            <ControlledCheckbox label={t('newContact')} name="isNewContact" />

            {watchNewContact && (
                <FormGrid size={{ xs: 12, md: 4 }}>
                    <ControlledInput name={`newContact.name`} label={t('nameLabel')} key="name" />
                    <ControlledInput name={`newContact.email`} label={t('Email')} key="email" />
                    <ControlledInput name={`newContact.phone`} label={t('Phone')} key="phone" />
                </FormGrid>
            )}
        </Section>
    );
};
